import React, { useMemo } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons'
import { ParsedMessageDisplay } from './ParsedMessageDisplay'
import { UnitCategoryDropdowns } from './UnitCategoryDropdowns'
import { cn } from '../shared/utils/cn'
import { UnitOverrides } from '../hooks/useMessageUnitOverrides'

// Import parseMessage from sigfox-decoder
import { parseMessage } from '@atcreate-inc/sigfox-decoder'

// Sensor config map type (keyed by port name)
interface SensorConfigMap {
  [portName: string]: {
    sensorConfigId?: number
    deviceId?: number
    sensorName?: string
    sensorProductId?: number // 1 = None (unconfigured)
    sensorNumber?: number
    unitCategoryId?: number
    unitId?: number
    conversionScale?: number
    conversionOffset?: number
  }
}

// Type definition for message (matching TMessageSigfoxOut from monorepo)
interface Message {
  MSG_RAW_ID: number
  SIGFOX_SNUM: number
  SIGFOX_TIME: number
  MSG_CONTENT: string
  messageType?: string
}

// Local utility to determine message type from hex content
function determineMessageType(messageContent: string): string {
  if (!messageContent || messageContent.length < 2) {
    return 'Unknown'
  }

  const byte = messageContent.slice(0, 2)
  const messageType = parseInt(byte, 16)

  switch (messageType) {
    case 0x04:
      return 'TestMessage'
    case 0x05:
      return 'SNTS'
    case 0x06:
      return 'SCHD'
    case 0x07:
      return 'DeviceConfig1'
    case 0x08:
      return 'DeviceConfig2'
    case 0x09:
      return 'DeviceConfig3'
    case 0x0a:
      return 'DeviceConfig4'
    case 0x0b:
      return 'DeviceConfig5'
    case 0x0c:
      return 'SensorConfig1'
    default:
      return 'Unknown'
  }
}

interface VirtualizedMessageTableProps {
  messages: Message[]
  height?: number
  className?: string
  showTestMessageColumn?: boolean
  selectedSensorPorts?: Set<string>
  sensorConfigs?: SensorConfigMap
  onLoadMore?: () => void
  hasMore?: boolean
  isLoadingMore?: boolean
  useViewportHeight?: boolean
  reservedHeight?: number
  // Sort order props (optional)
  sortOrder?: 'desc' | 'asc'
  onSortChange?: (order: 'desc' | 'asc') => void
  // Unit override props for display unit selection
  unitOverrides?: UnitOverrides
  presentUnitCategories?: number[]
  onUnitOverrideChange?: (categoryId: number, unitId: number) => void
  // Column visibility - hide raw hex on small screens
  showRawMessage?: boolean
  // Mobile portrait uses a two-line card layout instead of table columns
  isMobilePortrait?: boolean
}

export function VirtualizedMessageTable({
  messages,
  height = 600,
  className = '',
  showTestMessageColumn = false,
  selectedSensorPorts = new Set(),
  sensorConfigs = {},
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  useViewportHeight = false,
  reservedHeight = 0,
  sortOrder = 'desc',
  onSortChange,
  unitOverrides = {},
  presentUnitCategories = [],
  onUnitOverrideChange,
  showRawMessage = true,
  isMobilePortrait = false,
}: VirtualizedMessageTableProps) {
  // Create parent ref for virtualizer - this will be the scrolling container
  const parentRef = React.useRef<HTMLDivElement>(null)

  // Dynamic height calculation based on viewport and reserved space
  const [dynamicHeight, setDynamicHeight] = React.useState(height)

  React.useEffect(() => {
    if (!useViewportHeight) return

    const calculateHeight = () => {
      const viewportHeight = window.innerHeight
      const calculatedHeight = viewportHeight - reservedHeight
      setDynamicHeight(Math.max(300, calculatedHeight)) // Minimum height of 300px
    }

    calculateHeight()
    window.addEventListener('resize', calculateHeight)

    return () => window.removeEventListener('resize', calculateHeight)
  }, [useViewportHeight, reservedHeight])

  const tableHeight = useViewportHeight ? dynamicHeight : height

  // Setup virtualizer
  const virtualizer = useVirtualizer({
    count: messages.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 56, // Measured via measureElement; this is a mid-range estimate
    overscan: 5, // Render 5 extra items outside viewport for smooth scrolling
  })

  // Handle infinite scroll - load more when user actually scrolls near bottom
  React.useEffect(() => {
    if (!parentRef.current) return

    const handleScroll = () => {
      const scrollElement = parentRef.current
      if (!scrollElement) return

      const { scrollTop, scrollHeight, clientHeight } = scrollElement
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight

      // Only trigger when user has scrolled to 90% of the content
      if (scrollPercentage > 0.9 && hasMore && !isLoadingMore && onLoadMore) {
        onLoadMore()
      }
    }

    const scrollElement = parentRef.current
    scrollElement.addEventListener('scroll', handleScroll)

    return () => {
      scrollElement.removeEventListener('scroll', handleScroll)
    }
  }, [hasMore, isLoadingMore, onLoadMore])

  const virtualItems = virtualizer.getVirtualItems()

  // Memoize parsed messages for performance
  const parsedMessages = useMemo(() => {
    return messages.map((message) => ({
      ...message,
      // Trust the server's message type if available, otherwise determine from content
      messageType: (message as any).messageType || determineMessageType(message.MSG_CONTENT),
      parsedMessage: parseMessage(message.MSG_CONTENT),
    }))
  }, [messages])

  // Short date format for mobile
  const formatShortDateTime = (unixSeconds: number): string => {
    const d = new Date(unixSeconds * 1000)
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
      + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  }

  const renderMobileRow = (virtualRow: any, message: any) => {
    const isTestMessage = message.messageType === 'TestMessage'
    const isDeviceConfigUpdated = message.messageType === 'DeviceConfigUpdated'
    const isSensorConfigUpdated = message.messageType === 'SensorConfigUpdated'

    return (
      <div
        key={virtualRow.key}
        data-index={virtualRow.index}
        ref={virtualizer.measureElement}
        className={cn(
          'absolute top-0 left-0 w-full border-b border-gray-200 px-3 py-2',
          virtualRow.index % 2 === 0 ? 'bg-white' : 'bg-gray-50',
          isDeviceConfigUpdated && 'bg-emerald-50 border-emerald-200',
          isSensorConfigUpdated && 'bg-violet-50 border-violet-200'
        )}
        style={{
          minHeight: `${virtualRow.size}px`,
          transform: `translateY(${virtualRow.start}px)`,
        }}
      >
        {/* Row 1: meta info */}
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="text-xs text-gray-500 flex-shrink-0">#{message.SIGFOX_SNUM}</span>
          <span className="text-xs text-gray-400 flex-1">{formatShortDateTime(message.SIGFOX_TIME)}</span>
          <span
            className={cn(
              'px-2 py-0.5 text-xs rounded-full flex-shrink-0',
              isTestMessage
                ? 'bg-red-100 text-red-800'
                : isDeviceConfigUpdated
                  ? 'bg-emerald-100 text-emerald-800'
                  : isSensorConfigUpdated
                    ? 'bg-violet-100 text-violet-800'
                    : message.messageType.includes('Config')
                      ? 'bg-green-100 text-green-800'
                      : 'bg-blue-100 text-blue-800'
            )}
          >
            {message.messageType}
          </span>
        </div>
        {/* Row 2: parsed content - full width */}
        <div className="text-sm text-gray-900">
          <ParsedMessageDisplay
            messageContent={message.MSG_CONTENT}
            messageType={message.messageType}
            parsedData={message.parsedMessage}
            className="w-full"
            selectedSensorPorts={selectedSensorPorts}
            sensorConfigs={sensorConfigs}
            unitOverrides={unitOverrides}
          />
        </div>
      </div>
    )
  }

  const renderRow = (virtualRow: any, message: any) => {
    const isTestMessage = message.messageType === 'TestMessage'
    const isDeviceConfigUpdated = message.messageType === 'DeviceConfigUpdated'
    const isSensorConfigUpdated = message.messageType === 'SensorConfigUpdated'

    return (
      <div
        key={virtualRow.key}
        data-index={virtualRow.index}
        ref={virtualizer.measureElement}
        className={cn(
          'absolute top-0 left-0 w-full flex border-b border-gray-200 items-start',
          // Default alternating colors
          virtualRow.index % 2 === 0 ? 'bg-white' : 'bg-gray-50',
          // Override with special colors for aggregated messages
          isDeviceConfigUpdated && 'bg-emerald-50 border-emerald-200',
          isSensorConfigUpdated && 'bg-violet-50 border-violet-200'
        )}
        style={{
          minHeight: `${virtualRow.size}px`,
          transform: `translateY(${virtualRow.start}px)`,
        }}
      >
        {/* Device Seq.No. */}
        <div className="flex-shrink-0 w-20 px-3 py-2 text-sm text-gray-900 border-r border-gray-200 self-start">
          {message.SIGFOX_SNUM}
        </div>

        {/* Date Time */}
        <div className="flex-shrink-0 w-44 px-3 py-2 text-sm text-gray-900 border-r border-gray-200 self-start">
          {new Date(message.SIGFOX_TIME * 1000).toLocaleString()}
        </div>

        {/* MSG_CONTENT - hidden on mobile */}
        {showRawMessage && (
          <div className="flex-shrink-0 w-64 px-3 py-2 text-sm font-mono text-gray-900 border-r border-gray-200 self-start">
            <div className="whitespace-nowrap">{message.MSG_CONTENT}</div>
          </div>
        )}

        {/* Message Type */}
        <div className="flex-shrink-0 w-32 px-3 py-2 text-sm text-gray-900 border-r border-gray-200 self-start">
          <span
            className={cn(
              'px-2 py-1 text-xs rounded-full',
              isTestMessage
                ? 'bg-red-100 text-red-800'
                : isDeviceConfigUpdated
                  ? 'bg-emerald-100 text-emerald-800'
                  : isSensorConfigUpdated
                    ? 'bg-violet-100 text-violet-800'
                    : message.messageType.includes('Config')
                      ? 'bg-green-100 text-green-800'
                      : 'bg-blue-100 text-blue-800'
            )}
          >
            {message.messageType}
          </span>
        </div>

        {/* Parsed Message */}
        <div className="flex-1 px-3 py-2 text-sm text-gray-900 min-w-0">
          <ParsedMessageDisplay
            messageContent={message.MSG_CONTENT}
            messageType={message.messageType}
            parsedData={message.parsedMessage}
            className="w-full"
            selectedSensorPorts={selectedSensorPorts}
            sensorConfigs={sensorConfigs}
            unitOverrides={unitOverrides}
          />
        </div>
      </div>
    )
  }

  if (messages.length === 0) {
    return (
      <div className={cn('bg-white rounded-lg shadow border', className)}>
        <div className="p-8 text-center text-gray-500">No messages to display</div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'bg-white rounded-lg shadow overflow-hidden flex flex-col',
        'overflow-x-auto', // enable horizontal scroll
        className
      )}
      style={{ height: tableHeight }} // ensure height is set
    >
      {/* Table Header - hidden on mobile portrait */}
      <div className={cn(
        'flex bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wider flex-shrink-0',
        isMobilePortrait ? 'hidden' : 'min-w-[700px]'
      )}>
        <button
          onClick={() => onSortChange?.(sortOrder === 'desc' ? 'asc' : 'desc')}
          className={cn(
            'flex-shrink-0 w-20 px-3 py-2 border-r border-gray-200 flex items-center gap-1 text-left',
            onSortChange && 'hover:bg-gray-100 cursor-pointer'
          )}
          disabled={!onSortChange}
          title={sortOrder === 'desc' ? 'Showing newest first - click to show oldest first' : 'Showing oldest first - click to show newest first'}
        >
          <span>Device Seq.No.</span>
          <FontAwesomeIcon
            icon={sortOrder === 'desc' ? faSortDown : faSortUp}
            className="h-3 w-3 text-blue-600"
          />
        </button>
        <div className="flex-shrink-0 w-44 px-3 py-2 border-r border-gray-200">Date Time</div>
        {showRawMessage && (
          <div className="flex-shrink-0 w-64 px-3 py-2 border-r border-gray-200">MSG_CONTENT</div>
        )}
        <div className="flex-shrink-0 w-32 px-3 py-2 border-r border-gray-200">Message Type</div>
        <div className="flex-1 px-3 py-2">
          <div className="flex items-center justify-between gap-4">
            <span>{showTestMessageColumn ? 'Test Message' : 'Parsed Message'}</span>
            {!showTestMessageColumn && onUnitOverrideChange && presentUnitCategories.length > 0 && (
              <UnitCategoryDropdowns
                presentCategories={presentUnitCategories}
                unitOverrides={unitOverrides}
                onUnitChange={onUnitOverrideChange}
              />
            )}
          </div>
        </div>
      </div>

      {/* Virtualized Table Body */}
      <div ref={parentRef} className={cn('flex-1 overflow-auto min-h-0', !isMobilePortrait && 'min-w-[700px]')}>
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualItems.map((virtualRow) => {
            const message = parsedMessages[virtualRow.index]
            return isMobilePortrait
              ? renderMobileRow(virtualRow, message)
              : renderRow(virtualRow, message)
          })}

          {/* Loading indicator at bottom when fetching more */}
          {isLoadingMore && (
            <div
              style={{
                position: 'absolute',
                top: `${virtualizer.getTotalSize()}px`,
                left: 0,
                right: 0,
                height: '50px',
              }}
              className="flex items-center justify-center bg-gray-50 border-t border-gray-200"
            >
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <div className="text-sm text-gray-600">Loading more messages...</div>
              </div>
            </div>
          )}

          {/* "No more messages" indicator when infinite scroll reaches the end */}
          {!hasMore && !isLoadingMore && messages.length > 0 && (
            <div
              style={{
                position: 'absolute',
                top: `${virtualizer.getTotalSize()}px`,
                left: 0,
                right: 0,
                height: '60px',
              }}
              className="flex items-center justify-center bg-gradient-to-b from-gray-50 to-gray-100 border-t border-gray-200"
            >
              <div className="flex items-center text-sm text-gray-500">
                <span>All messages loaded - no more to show</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer with message count and infinite scroll status */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-sm text-gray-600 flex-shrink-0 flex justify-between items-center">
        <span>Showing {messages.length} messages</span>
        {hasMore && <span className="text-xs text-blue-600">Scroll down to load more</span>}
      </div>
    </div>
  )
}
